import { format, isValid, parse } from "date-fns";

import { LOCALIZE_DEALERS } from "./options";
import type {
  FormValues,
  OptionsGroupIProps,
  OptionsIProps,
} from "./schema/types";

export type FastItem = "day" | "week" | "month" | null;

export interface SummaryURLParams {
  from?: string;
  values?: string | string[];
  start?: string;
  end?: string;
  period?: string;
}

const DATE_FMT = "yyyy-MM-dd";

const asArray = (value: FormValues["dealerSelect"]): OptionsIProps[] => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

export const filtersToURL = (
  data: FormValues,
  fastItem: FastItem,
): SummaryURLParams => {
  const values = asArray(data.dealerSelect).map((item) => String(item.value));

  return {
    from: data.selectFrom?.value ? String(data.selectFrom.value) : "",
    values: values.length ? values : "",
    start: data.startDate ? format(new Date(data.startDate), DATE_FMT) : "",
    end: data.endDate ? format(new Date(data.endDate), DATE_FMT) : "",
    period: fastItem ?? "",
  };
};

const parseDate = (value: string | undefined, fallback: Date): Date => {
  if (!value) return fallback;
  const parsed = parse(value, DATE_FMT, new Date());
  return isValid(parsed) ? parsed : fallback;
};

export const filtersFromURL = (
  params: SummaryURLParams,
  options: OptionsGroupIProps,
  fallback: FormValues,
): { values: FormValues; fastItem: FastItem } | null => {
  const hasFilters =
    params.from || params.values || params.start || params.end || params.period;
  if (!hasFilters) return null;

  const fromValue = params.from || String(fallback.selectFrom.value);
  const selectFrom =
    LOCALIZE_DEALERS.find((option) => option.value === fromValue) ??
    fallback.selectFrom;

  const rawValues = params.values
    ? Array.isArray(params.values)
      ? params.values
      : [params.values]
    : [];

  const categoryOptions = options[fromValue as keyof OptionsGroupIProps] || [];
  const categoryOptionsByValue = new Map(
    categoryOptions.map((option) => [String(option.value), option]),
  );

  const dealerSelect: OptionsIProps[] = rawValues.map((value) => {
    return (
      categoryOptionsByValue.get(String(value)) ?? {
        label: String(value),
        value,
      }
    );
  });

  const fastItem: FastItem = (["day", "week", "month"] as const).includes(
    params.period as "day" | "week" | "month",
  )
    ? (params.period as FastItem)
    : null;

  return {
    values: {
      selectFrom,
      dealerSelect: dealerSelect.length ? dealerSelect : fallback.dealerSelect,
      startDate: parseDate(params.start, fallback.startDate),
      endDate: parseDate(params.end, fallback.endDate),
    },
    fastItem,
  };
};
